import { type FC, useEffect, useRef, useState } from 'react';
import {
  Flex,
  Button,
  Text,
  PinInput,
  PinInputField,
  HStack,
} from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { BadgeErrorForm } from '@/modules/Auth/ui/BadgeErrorForm';
import { formatTime } from '@/utils/date';
import { IfRender } from '@/utils/jsx';
import { Aeonik } from '@/themes/fonts';

export const OtpCodeInput: FC<OtpCodeInputProps> = ({
  loading = false,
  error = '',
  otpValidationTime = 120,
  isDisabled = false,
  handleCode,
  handleExpiration,
  resendSendOtp,
}) => {
  const t = useT();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const [remainingTime, setRemainingTime] = useState(otpValidationTime);
  const startTimer = () => {
    intervalRef.current = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 1 && intervalRef?.current) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  const handleResendCode = () => {
    resendSendOtp();
    setRemainingTime(otpValidationTime);
    startTimer();
  };

  useEffect(() => {
    startTimer();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (remainingTime === 0 && handleExpiration) {
      handleExpiration();
    }
  }, [remainingTime, handleExpiration]);

  return (
    <Flex
      flexDir="column"
      alignItems="center"
      w="full"
      gap="2xl"
      sx={{
        input: {
          w: '3rem',
          h: '3rem',
          outline: 'none !important',
          borderColor: error ? 'alert.error' : 'border.primary',
          _focus: {
            outline: 'none !important',
            borderColor: error ? 'alert.error' : 'border.primary',
            boxShadow: 'none',
          },
          '.code-input-otp': {
            fontFamily: Aeonik.style.fontFamily,
            fontWeight: 'normal',
          },
        },
      }}
    >
      <HStack>
        <PinInput
          isDisabled={isDisabled}
          autoFocus
          manageFocus
          errorBorderColor="alert.error"
          onComplete={handleCode}
        >
          {Array.from({ length: 6 }, (_, index) => (
            <PinInputField key={index} />
          ))}
        </PinInput>
      </HStack>
      <IfRender condition={!!error}>
        <BadgeErrorForm htmlError={error ?? ''} />
      </IfRender>
      <Flex direction="column" gap="xs" alignItems="center">
        <Button
          onClick={handleResendCode}
          variant="naked"
          size="l"
          color="content.brand"
          fontWeight="semibold"
          isDisabled={remainingTime > 0}
          transition="all 300ms ease-in-out"
          isLoading={loading}
        >
          {t('screens.otp.resendCode')}
        </Button>
        {remainingTime > 0 && (
          <Text
            data-testid="otp-code-input-time-remaining"
            size="l"
            transition="all 300ms ease-in-out"
          >
            {formatTime(remainingTime)}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

type OtpCodeInputProps = {
  loading?: boolean;
  error?: string | null;
  otpValidationTime?: number;
  isDisabled?: boolean;
  handleCode: (code: string) => void;
  handleExpiration?: () => void;
  resendSendOtp: () => void;
};
